import { getApiV1Url } from '../config/api';
import { getUserInfo } from './auth';
import type { ProjectConfigItem, ProfileItem } from './projectConfig';

function authHeaders(): Record<string, string> {
  const userInfo = getUserInfo();
  return {
    'Content-Type': 'application/json',
    ...(userInfo && {
      'X-Account-Code': userInfo.accountCode,
      'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
    }),
  };
}

function appProfilesPath(projectCode: string, appCode: string): string {
  return `/projects/${encodeURIComponent(projectCode)}/apps/${encodeURIComponent(appCode)}/profiles`;
}

/** API 조회 응답의 프로필 한 건 */
interface ProfileResponseItem {
  category: string;
  settingKey: string;
  settingValue: { type: string; value: string[] | string };
  projectCode?: string;
  appCode?: string;
  [key: string]: unknown;
}

export interface AppConfigResponse {
  projectCode: string;
  appCode: string;
  items: ProjectConfigItem[];
}

/**
 * 앱 코드로 해당 앱의 프로필(설정) 전체 조회
 * GET /api/v1/projects/{projectCode}/apps/{appCode}/profiles
 */
export async function getAppConfig(projectCode: string, appCode: string): Promise<AppConfigResponse> {
  const headers = authHeaders();
  const url = getApiV1Url(appProfilesPath(projectCode, appCode));
  const response = await fetch(url, { method: 'GET', headers });
  if (!response.ok) {
    throw new Error(`앱 설정 조회 실패: ${response.status}`);
  }
  const json = await response.json();
  const data = json.data ?? json;
  const profiles: ProfileResponseItem[] = data.profiles ?? [];
  const items: ProjectConfigItem[] = profiles.map((p) => {
    let val = p.settingValue?.value ?? '';
    if (p.settingValue?.type === 'ENUM_SINGLE' && Array.isArray(val)) {
      val = val.length ? val[0] : '';
    }
    return {
      configId: `${p.category}_${p.settingKey}`,
      value: val,
    };
  });
  const resProjectCode = data.projectCode ?? projectCode;
  const resAppCode = data.appCode ?? appCode;
  return { projectCode: resProjectCode, appCode: resAppCode, items };
}

/**
 * 앱 프로필 일괄 저장
 * POST /api/v1/projects/{projectCode}/apps/{appCode}/profiles/bulk
 */
export async function updateAppProfilesBulk(
  projectCode: string,
  appCode: string,
  profiles: ProfileItem[]
): Promise<void> {
  const headers = authHeaders();
  const url = getApiV1Url(`${appProfilesPath(projectCode, appCode)}/bulk`);
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ profiles }),
  });
  if (!response.ok) {
    throw new Error(`앱 설정 저장 실패: ${response.status}`);
  }
}

/**
 * 단일 설정 키 저장 (필요 시 사용)
 * PUT /api/v1/projects/{projectCode}/apps/{appCode}/profiles/{category}/{settingKey}
 */
export async function putAppProfile(
  projectCode: string,
  appCode: string,
  category: string,
  settingKey: string,
  body: ProfileItem
): Promise<void> {
  const headers = authHeaders();
  const url = getApiV1Url(
    `${appProfilesPath(projectCode, appCode)}/${encodeURIComponent(category)}/${encodeURIComponent(settingKey)}`
  );
  const response = await fetch(url, {
    method: 'PUT',
    headers,
    body: JSON.stringify({
      settingValue: body.settingValue,
    }),
  });
  if (!response.ok) {
    throw new Error(`앱 설정 저장 실패: ${response.status}`);
  }
}
